import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Mirrors app/views/icons/_checkmark.html.erb so the optimistic checked state
// matches the server-rendered one.
const CHECK_SVG = `<svg class="size-5" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true"><path clip-rule="evenodd" fill-rule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"></path></svg>`

// Drives the Daily Routine Guidance surfaces (Routine tab "Today" view + Home
// card). Interactions are applied eagerly client-side: tapping a row flips its
// circle and recomputes the phase/overall counters instantly; tapping a phase
// checkbox does the same for every due row in that phase.
//
// The network call is owned here rather than by a background <form>, because the
// check-off must be concurrency-safe under rapid tapping:
//   - The request is intent-based: it posts the row's *desired* state, and the
//     server sets it idempotently (rather than blindly toggling). Reordered,
//     duplicated, or retried requests all converge.
//   - At most one request per row is in flight. Taps that land while a request is
//     in flight only update the desired state; when it settles, a single trailing
//     request is sent if the user's final intent differs from what was last sent.
//     So the last write always reflects the last tap.
//   - On failure, the row is reconciled back to the last server-confirmed state,
//     so it never lingers showing an unsaved change.
//
// The server reconciles the authoritative progress counters via the Turbo Stream
// response (replacing the phase/overall progress, never the row itself, so the
// product image never flickers). The controller only flips a number and toggles
// visibility of server-rendered states — it never builds progress markup.
export default class extends Controller {
  static targets = [
    "circle",
    "phaseProgress",
    "phaseDone",
    "phaseCount",
    "phaseCompleted",
    "phaseStatus",
    "phaseRing",
    "phaseToggle",
    "overallProgress",
    "overallCompleted",
    "overallRing"
  ]

  connect() {
    // Per-row request state, keyed by the row button element:
    //   { desired, lastSent, confirmed, inFlight }
    // `confirmed` tracks the last state the server acknowledged (seeded from the
    // server-rendered DOM), so a failed request can roll the row back to truth.
    this.rows = new Map()
    this.phases = new Map()
    this.phaseRingAnimationFrames = []
    this.preserveMorphAttribute = this.preserveMorphAttribute.bind(this)
    document.addEventListener("turbo:before-morph-attribute", this.preserveMorphAttribute)

    // A newly-rendered card (including the Turbo replacement after phase completion)
    // starts with an empty ring. Advancing to the server-rendered offset on the
    // following frame lets the browser interpolate the stroke instead of
    // painting the completed percentage all at once.
    this.phaseRingTargets.forEach((ring) => {
      const firstFrame = requestAnimationFrame(() => {
        const secondFrame = requestAnimationFrame(() => {
          ring.style.strokeDashoffset = ring.dataset.progressOffset || "100"
        })
        this.phaseRingAnimationFrames.push(secondFrame)
      })
      this.phaseRingAnimationFrames.push(firstFrame)
    })

    // Seed each phase's completion state from the server-rendered DOM so the
    // first toggle only emits a collapse/expand event on a real transition,
    // never spuriously on an already-settled phase. On the multi-routine Today
    // layout two routines share a Morning / Evening column, so completion is
    // tracked per routine+phase (phaseKey), not per phase alone.
    this.phaseComplete = {}
    this.phaseProgressTargets.forEach((progress) => {
      const due = parseInt(progress.dataset.due || "0", 10)
      this.phaseComplete[this.phaseKey(progress)] =
        due > 0 && this.completedFor(progress) >= due
    })

    // The week strip follows DiaryEntry completion semantics: a phase counts as
    // logged as soon as any product in it is checked, not only when every due
    // product is complete. Seed that separately from the accordion's completion
    // state so the first individual tap can fill the day ring optimistically.
    this.phaseLogged = {}
    this.dayStripPhases().forEach((phase) => {
      this.phaseLogged[phase] = this.phaseHasLogs(phase)
    })
  }

  disconnect() {
    this.phaseRingAnimationFrames?.forEach((frame) => cancelAnimationFrame(frame))
    document.removeEventListener("turbo:before-morph-attribute", this.preserveMorphAttribute)
  }

  // Alpine owns the accordion's live state and the phase body's visibility
  // styles. A server morph should reconcile its contents without resetting
  // those runtime-only attributes and replaying the open/close transition.
  preserveMorphAttribute(event) {
    const target = event.target
    const attribute = event.detail.attributeName

    if (!this.element.contains(target)) return

    if (target.matches("[data-routine-guidance-accordion]") && attribute === "x-data") {
      event.preventDefault()
    }

    if (
      target.matches("[data-routine-guidance-phase-body]") &&
      [ "style", "x-cloak" ].includes(attribute)
    ) {
      event.preventDefault()
    }
  }

  toggle(event) {
    const row    = event.currentTarget
    const circle = row.querySelector('[data-routine-guidance-target="circle"]')
    if (!circle) return

    const previous = circle.dataset.checked === "true"
    const desired  = !previous

    this.render(row, desired)
    this.bounce(circle)
    this.recount()
    this.sync(row, desired, previous)
  }

  togglePhase(event) {
    const toggle   = event.currentTarget
    const phase    = toggle.dataset.phase
    const desired  = toggle.dataset.checked !== "true"
    const circles  = this.countedCircles(phase)
    const progress = this.phaseProgressTargets.filter((item) => item.dataset.phase === phase)
    const previous = {
      circles: new Map(circles.map((circle) => [ circle, circle.dataset.checked === "true" ])),
      progress: new Map(progress.map((item) => [ item, item.dataset.completed ]))
    }

    this.renderPhase(this.phaseCirclesToCheck(circles, desired), desired)
    progress.forEach((item) => {
      item.dataset.completed = desired ? item.dataset.due : "0"
    })
    this.recount()
    this.syncPhase(toggle, desired, previous)
  }

  // Record the row's desired end state and, unless a request is already in flight
  // for this row, start one. Rapid taps collapse into the in-flight request plus a
  // single trailing send carrying the final intent.
  sync(row, desired, previousConfirmed) {
    let state = this.rows.get(row)

    if (!state) {
      // Seed `confirmed` with the state before this first tap, i.e. what the
      // server rendered. Subsequent taps reuse the tracked state.
      state = { desired, lastSent: null, confirmed: previousConfirmed, inFlight: false }
      this.rows.set(row, state)
    }

    state.desired = desired

    if (!state.inFlight) this.send(row, state)
  }

  async send(row, state) {
    state.inFlight = true
    state.lastSent = state.desired

    let responseHTML = null
    try {
      const response = await this.post(row, state.lastSent)
      if (response.ok) {
        responseHTML = await response.text()
        state.confirmed = state.lastSent
      }
    } catch (_error) {
      responseHTML = null
    } finally {
      state.inFlight = false
    }

    // The user changed their mind while this request was in flight: send one more
    // request carrying the newest intent without rendering the now-stale stream.
    // The endpoint is idempotent, so this converges on the final tap regardless
    // of how the responses interleave.
    if (state.desired !== state.lastSent) {
      this.send(row, state)
      return
    }

    if (responseHTML !== null) {
      if (responseHTML) Turbo.renderStreamMessage(responseHTML)
      this.dispatch("logged", { target: window })
      return
    }

    // Settled with no newer intent and a failed request. Reconcile the row to the
    // last server-confirmed state so it never shows an unsaved change.
    if (state.confirmed !== state.desired) {
      this.render(row, state.confirmed)
      this.recount()
    }
  }

  post(row, logged) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body      = new FormData()

    body.append("surface", row.dataset.quickLogSurface || "")
    body.append("logged", logged ? "true" : "false")
    if (row.dataset.quickLogDate) body.append("date", row.dataset.quickLogDate)

    return fetch(row.dataset.quickLogUrl, {
      method: "POST",
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest"
      },
      body: body,
      credentials: "same-origin"
    })
  }

  syncPhase(toggle, desired, previousConfirmed) {
    const key = toggle.dataset.phase
    let state = this.phases.get(key)

    if (!state) {
      state = { desired, lastSent: null, confirmed: previousConfirmed, inFlight: false }
      this.phases.set(key, state)
    }

    state.desired = desired

    if (!state.inFlight) this.sendPhase(toggle, state)
  }

  async sendPhase(toggle, state) {
    state.inFlight = true
    state.lastSent = state.desired

    let responseHTML = null
    try {
      const response = await this.postPhase(toggle, state.lastSent)
      if (response.ok) {
        responseHTML = await response.text()
        state.confirmed = state.lastSent
      }
    } catch (_error) {
      responseHTML = null
    } finally {
      state.inFlight = false
    }

    // If another tap landed while the request was running, skip the now-stale
    // stream and send only the final desired state. This avoids a visible flash
    // back to the first tap while rapid input is settling.
    if (state.desired !== state.lastSent) {
      this.sendPhase(toggle, state)
      return
    }

    if (responseHTML !== null) {
      if (responseHTML) Turbo.renderStreamMessage(responseHTML)
      this.dispatch("logged", { target: window })
      return
    }

    this.restorePhase(toggle.dataset.phase, state.confirmed)
    this.recount()
  }

  postPhase(toggle, logged) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body      = new FormData()

    body.append("phase", toggle.dataset.phase)
    body.append("date", toggle.dataset.date)

    return fetch(toggle.dataset.phaseLogUrl, {
      method: logged ? "POST" : "DELETE",
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest"
      },
      body: body,
      credentials: "same-origin"
    })
  }

  // Checking a whole phase checks one product per step: an interchangeable step
  // that already has a checked member is left alone, otherwise only its first
  // member is checked (the server logs the same one). Unchecking clears all.
  phaseCirclesToCheck(circles, desired) {
    if (!desired) return circles

    const settled = new Set(
      circles
        .filter((circle) => circle.dataset.stepGroup && circle.dataset.checked === "true")
        .map((circle) => circle.dataset.stepGroup)
    )

    return circles.filter((circle) => {
      const group = circle.dataset.stepGroup
      if (!group) return true
      if (settled.has(group)) return false

      settled.add(group)
      return true
    })
  }

  renderPhase(circles, checked) {
    circles.forEach((circle) => {
      const row = circle.closest("button")
      if (row) this.render(row, checked)
    })
  }

  restorePhase(phase, confirmed) {
    const circles = this.countedCircles(phase)

    circles.forEach((circle) => {
      const checked = typeof confirmed === "object" ? confirmed.circles.get(circle) : confirmed
      const row = circle.closest("button")
      if (row && checked !== undefined) this.render(row, checked)
    })

    this.phaseProgressTargets
      .filter((progress) => progress.dataset.phase === phase)
      .forEach((progress) => {
        if (typeof confirmed === "object") {
          progress.dataset.completed = confirmed.progress.get(progress)
        } else {
          progress.dataset.completed = confirmed ? progress.dataset.due : "0"
        }
      })
  }

  // Apply the full "done" / "not done" look for a row: the check circle, an
  // animated strike-through on the name, and a dimmed product image.
  render(row, checked) {
    const circle = row.querySelector('[data-routine-guidance-target="circle"]')
    const name   = row.querySelector("[data-guidance-name]")
    const image  = row.querySelector("[data-guidance-image]")

    if (circle) {
      circle.dataset.checked = checked ? "true" : "false"
      circle.setAttribute("aria-label", checked ? "Mark not done" : "Mark done")

      // A circle can name its own unchecked look (rows in a Today step group sit
      // on a gray inset, where the default gray fill would disappear).
      const unchecked = (circle.dataset.uncheckedClass || "bg-warm-gray-100 text-warm-gray-500 border-warm-gray-100").split(" ")

      if (checked) {
        circle.classList.add("!bg-indigo-600", "text-white", "border-indigo-600")
        circle.classList.remove(...unchecked)
        circle.innerHTML = CHECK_SVG
      } else {
        circle.classList.remove("!bg-indigo-600", "text-white", "border-indigo-600")
        circle.classList.add(...unchecked)
        circle.innerHTML = ""
      }
    }

    if (name) {
      name.classList.toggle("is-done", checked)
      name.classList.toggle("text-warm-gray-400", checked)
      name.classList.toggle("text-warm-gray-800", !checked)
    }

    if (image) {
      image.classList.toggle("opacity-50", checked)
    }
  }

  // Snappy spring pop on the circle. Re-trigger by clearing the class and
  // forcing a reflow so a rapid re-tap replays the animation from the start.
  bounce(circle) {
    circle.classList.remove("guidance-pop")
    void circle.offsetWidth
    circle.classList.add("guidance-pop")
  }

  recount() {
    this.phaseProgressTargets.forEach((progress) => {
      const due = parseInt(progress.dataset.due || "0", 10)
      if (due <= 0) return

      const completed = this.completedFor(progress)
      const done      = completed >= due

      progress.dataset.completed = completed

      const doneEl      = progress.querySelector('[data-routine-guidance-target="phaseDone"]')
      const completedEl = progress.querySelector('[data-routine-guidance-target="phaseCompleted"]')
      const statusEl    = progress.querySelector('[data-routine-guidance-target="phaseStatus"]')
      const toggle      = progress.querySelector('[data-routine-guidance-target="phaseToggle"]')

      if (statusEl) {
        statusEl.textContent = done ? `Done · ${completed} of ${due}` : `${completed} of ${due} done`
      } else if (completedEl) {
        completedEl.textContent = completed
      }
      if (toggle) this.renderPhaseToggle(toggle, done)
      const ring = this.phaseRingTargets.find((candidate) => {
        return candidate.dataset.phase === progress.dataset.phase &&
          (candidate.dataset.routine || "") === (progress.dataset.routine || "")
      })
      if (ring) {
        const percent = Math.round((completed / due) * 100)
        const offset  = String(100 - percent)
        ring.dataset.progressOffset = offset
        ring.style.strokeDashoffset = offset
      }
      // The count stays visible in both states; only the trailing check pill flips.
      if (doneEl) doneEl.classList.toggle("hidden", !done)

      // On a completion transition, tell this phase's section to gracefully
      // collapse (or re-expand). Dispatched on the progress element so the event
      // bubbles up to that phase's Alpine component and no sibling's.
      const key = this.phaseKey(progress)
      if (this.phaseComplete[key] !== done) {
        this.phaseComplete[key] = done
        const detail = {
          phase: progress.dataset.phase,
          complete: done
        }

        this.dispatch("phase-completion", {
          target: progress,
          prefix: false,
          detail: detail
        })
      }
    })

    this.updateDayStripProgress()
    if (this.hasOverallProgressTarget && this.hasOverallCompletedTarget) {
      const due = parseInt(this.overallProgressTarget.dataset.due || "0", 10)
      if (due > 0) {
        const completed = this.overallCompletedCount()
        this.overallCompletedTarget.textContent = completed
        // Nudge the ring's fill to match (pathLength=100, so offset = 100 - percent).
        if (this.hasOverallRingTarget) {
          const percent = Math.round((completed / due) * 100)
          this.overallRingTarget.style.strokeDashoffset = String(100 - percent)
        }
      }
    }
  }

  renderPhaseToggle(toggle, checked) {
    const phaseLabel = toggle.dataset.phase === "morning" ? "morning" : "evening"

    toggle.dataset.checked = checked ? "true" : "false"
    toggle.setAttribute("aria-label", `Mark ${phaseLabel} ${checked ? "not done" : "done"}`)
    toggle.classList.toggle("bg-indigo-950", checked)
    toggle.classList.toggle("text-white", checked)
    toggle.classList.toggle("border", !checked)
    toggle.classList.toggle("border-warm-gray-300", !checked)
    toggle.classList.toggle("text-warm-gray-400", !checked)
    toggle.innerHTML = checked ? CHECK_SVG : ""
  }

  // Count checked, counted circles. `phase` (null = both) and `routine` (null =
  // every routine) narrow the set so a phase's "X of N" reflects just that
  // routine's column on the multi-routine layout, while the overall ring still
  // sums the whole day (both null).
  completedCount(phase, routine = null) {
    return this.checkedSteps(this.circleTargets.filter((circle) => {
      if (circle.dataset.counts !== "true") return false
      if (phase !== null && circle.dataset.phase !== phase) return false
      if (routine !== null && circle.dataset.routine !== routine) return false
      return true
    }))
  }

  // Checked steps among these circles. Interchangeable products share one step
  // (the same data-step-group), so it counts once when any of them is checked.
  checkedSteps(circles) {
    const steps = new Set()

    circles.forEach((circle, index) => {
      if (circle.dataset.checked === "true") steps.add(circle.dataset.stepGroup || `circle-${index}`)
    })

    return steps.size
  }

  countedCircles(phase) {
    return this.circleTargets.filter((circle) => {
      return circle.dataset.counts === "true" && circle.dataset.phase === phase
    })
  }

  completedFor(progress) {
    const routine = progress.dataset.routine || null
    const circles = this.countedCircles(progress.dataset.phase).filter((circle) => {
      return routine === null || circle.dataset.routine === routine
    })

    if (circles.length > 0) {
      return this.checkedSteps(circles)
    }

    return parseInt(progress.dataset.completed || "0", 10)
  }

  overallCompletedCount() {
    if (this.hasPhaseStatusTarget) {
      return this.phaseProgressTargets.reduce((total, progress) => {
        return total + parseInt(progress.dataset.completed || "0", 10)
      }, 0)
    }

    return this.completedCount(null)
  }

  // Keep the week strip aligned with the diary entry: one or more logged
  // products means this AM/PM phase is active. This deliberately includes
  // optional and off-schedule rows because the server includes them too.
  updateDayStripProgress() {
    this.dayStripPhases().forEach((phase) => {
      const logged = this.phaseHasLogs(phase)
      if (this.phaseLogged[phase] === logged) return

      this.phaseLogged[phase] = logged
      this.dispatch("day-progress", {
        target: this.element,
        prefix: false,
        detail: { phase: phase, logged: logged }
      })
    })
  }

  dayStripPhases() {
    return [ ...new Set(
      this.phaseProgressTargets
        .filter((progress) => progress.dataset.updateDayStrip === "true")
        .map((progress) => progress.dataset.phase)
    ) ]
  }

  phaseHasLogs(phase) {
    return this.circleTargets.some((circle) => {
      return circle.dataset.phase === phase && circle.dataset.checked === "true"
    })
  }

  // A stable key for a phase-progress element: routine id + time-of-day. On the
  // single-routine / Home-card surfaces the routine part is empty, so it collapses
  // to the original per-phase key.
  phaseKey(progress) {
    return `${progress.dataset.routine || ""}:${progress.dataset.phase}`
  }
}
